/**
 * `detail.ledger` - the provenance sibling every asset-register event carries.
 *
 * The envelope has no columns for actor, source, recorded-at or estimate flags, so these fields
 * live in `detail`. `detail` is opaque JSON to the API and is capped at 20480 bytes.
 *
 * Shape: `devdocs/features/asset-register/design/events-schema.md`.
 */

/** How the fact reached us, not who typed it. */
export const EVENT_SOURCES = ['manual', 'mobile', 'scan', 'telemetry', 'integration', 'system', 'import']

export const LEDGER_VERSION = 1

export const isEventSource = source => EVENT_SOURCES.includes(source)

/**
 * A user actor with no user id is worse than no user actor: it asserts a person was identified when
 * none was. Callers resolve the id against a farm's own user list and legitimately get nothing back
 * - the list has not loaded, or the signed-in account is not a member of this farm - and the
 * register already omits `userIds` in that case rather than sending a broken reference.
 *
 * So the id is dropped and the kind is kept. `{ kind: 'user' }` says a person did this and we cannot
 * name them, which is true. Falling back to `{ kind: 'system' }` would say no person was involved,
 * which is not.
 */
const normaliseActor = actor => {
    if (!actor || typeof actor !== 'object') return { kind: 'system' }
    if (actor.kind !== 'user') return actor
    return actor.userId == null ? { kind: 'user' } : actor
}

/**
 * Build `detail.ledger`.
 *
 * `time` and `recordedAt` are different facts and must both be supplied by the caller: `time` is
 * when the thing happened, `recordedAt` is when it was captured. Offline capture makes them
 * diverge, and sync must never rewrite `time` to close the gap. `backdated` is the
 * flag that keeps the divergence visible instead.
 *
 * **`backdated` is a fact, not an intent.** It says the event was recorded after it happened. It
 * does **not** say a person deliberately backdated anything, and a reader must not present it that
 * way: a form whose time control defaults to a window in the past sets it without the user touching
 * it. Resist the temptation to add a tolerance threshold - a threshold makes the flag lie in the
 * other direction, marking a genuinely late record as timely. Where intent matters, the register
 * that knows the difference should record it, not infer it here.
 *
 * No clock is read here. Both timestamps are arguments so the builder stays pure and testable, and
 * so an offline queue can stamp `recordedAt` at capture rather than at flush.
 *
 * @param {{
 *   time: string,
 *   recordedAt: string,
 *   source: string,
 *   actor?: { kind: 'user'|'system', userId?: number },
 *   position?: Object,
 *   paddockId?: number,
 *   reason?: string,
 *   attachmentIds?: Array<string|number>,
 *   estimatedFields?: string[],
 *   sessionId?: string,
 *   deviceId?: string,
 *   reversesEventId?: number|string,
 * }} input
 *
 * `sessionId` groups several events into one round with nothing else to consult. A stocktake
 * spanning many lots or holdings (`CR-STK-06`) is not a stored object of its own - it is every
 * per-lot adjustment line from one count sharing this value, so a reader groups by `sessionId`
 * instead of joining against a header row that was never written. See `stocktake.js`.
 * @returns {Object} detail.ledger
 */
/** The calendar date `recordedAt` fell on where it was recorded - the same calendar the date picker used. */
const localDateOf = iso => {
    const d = new Date(iso)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * A picked date becomes a real UTC instant, in the browser's timezone. Today's date is the moment of
 * recording; any other date is local noon, so its UTC day is the picked day for any farm timezone.
 * `localDate` carries the day exactly as the person saw it - read a day through `eventDay`, never by
 * slicing `time`, because a real instant's UTC day can differ from the farm's day. A full timestamp
 * passes through untouched.
 * @returns {{ time: string, localDate?: string, timePrecision?: 'date' }}
 */
export const resolveEventTime = (time, recordedAt) => {
    if (typeof time !== 'string' || !DATE_ONLY.test(time)) return { time }
    const [y, m, d] = time.split('-').map(Number)
    const instant = recordedAt && time === localDateOf(recordedAt) ? new Date(recordedAt) : new Date(y, m - 1, d, 12)
    return { time: instant.toISOString(), localDate: time, timePrecision: 'date' }
}

/** The day an event belongs to: the day the person picked, else the UTC day of `time` (events before localDate). */
export const eventDay = event => event?.detail?.ledger?.localDate || String(event?.time || '').slice(0, 10)

export const buildEventLedger = ({ time, recordedAt, source, actor, position, paddockId, reason, attachmentIds, estimatedFields, sessionId, deviceId, reversesEventId, timePrecision, localDate } = {}) => {
    if (!recordedAt) throw new Error('buildEventLedger: recordedAt is required')
    if (!isEventSource(source)) throw new Error(`buildEventLedger: unknown source "${source}"`)

    const ledger = {
        recordedAt,
        // `timePrecision: 'date'`: `time` is a picked date stored as 00:00Z, so it is backdated only when
        // that date is before the local date it was recorded on - not every time after 10 am in AEST.
        backdated: Boolean(time && recordedAt && (timePrecision === 'date' ? String(localDate || time).slice(0, 10) < localDateOf(recordedAt) : new Date(time) < new Date(recordedAt))),
        ...(timePrecision === 'date' ? { timePrecision } : {}),
        ...(localDate ? { localDate } : {}),
        source,
        actor: normaliseActor(actor),
        v: LEDGER_VERSION,
    }

    // Optional keys are omitted rather than set to null. Absence has to stay distinguishable from
    // an explicit empty, which is the same rule A10 applies to `detail` keys generally.
    if (position !== undefined) ledger.position = position
    if (paddockId !== undefined) ledger.paddockId = paddockId
    if (reason !== undefined) ledger.reason = reason
    if (Array.isArray(attachmentIds) && attachmentIds.length) ledger.attachmentIds = [...attachmentIds]
    if (Array.isArray(estimatedFields) && estimatedFields.length) ledger.estimatedFields = [...estimatedFields]
    if (sessionId !== undefined) ledger.sessionId = sessionId
    if (deviceId !== undefined) ledger.deviceId = deviceId
    if (reversesEventId !== undefined && reversesEventId !== null && reversesEventId !== '') {
        if (!reason) throw new Error('buildEventLedger: a reversal needs a reason')
        ledger.reversesEventId = reversesEventId
    }

    return ledger
}

// ── Reversal (AR-MOD-02, AR-EVT-03) ─────────────────────────────────────────────────────────────

/**
 * Craig, 17 Sep 2026, *D2 + reversal marker*: a figure is never edited in place. It is undone by a
 * second event carrying `detail.ledger.reversesEventId` and a reason, and the original stays.
 *
 * The marker is on `detail.ledger`, not a `*Reverse` type per verb (`LED-EVT-03`), so readers ask
 * `isReversal` rather than knowing each family's carrier.
 */
export const isReversal = event => {
    const id = event?.detail?.ledger?.reversesEventId
    return id !== undefined && id !== null && id !== ''
}

/** Ids, as strings, of every event some other event in the list reverses. */
export const reversedEventIds = (events = []) => new Set((events || []).filter(isReversal).map(event => String(event.detail.ledger.reversesEventId)))

/** The reversal that undoes this event, when the list holds one. */
export const reversalOf = (event, events = []) => (event?.id == null ? null : (events || []).find(other => isReversal(other) && String(other.detail.ledger.reversesEventId) === String(event.id)) || null)

/**
 * The events a figure is computed from: every reversal and every event one of them reverses is dropped.
 *
 * Dropping the pair, rather than applying the reversal as a negative line, is what lets one rule serve
 * a meter reading, a move and a status change as well as a quantity - none of those has a sign. A
 * history view does not call this; it shows both, marked.
 *
 * A reversal whose original is outside the list (a narrower period, a truncated read) is still dropped:
 * it carries no fact of its own.
 */
export const withoutReversed = (events = []) => {
    const reversed = reversedEventIds(events)
    return (events || []).filter(event => !isReversal(event) && !(event?.id != null && reversed.has(String(event.id))))
}

export default buildEventLedger
